import React, { useState } from 'react';
import { useMoveBulk } from '../hooks/useObjets';
import { useZones } from '../hooks/useZones';
import { useBoxes } from '../hooks/useBoxes';

interface BulkMoveModalProps {
  isOpen: boolean;
  onClose: () => void;
  selectedIds: string[];
}

const BulkMoveModal: React.FC<BulkMoveModalProps> = ({ isOpen, onClose, selectedIds }) => {
  const moveBulkMutation = useMoveBulk();
  const { data: zones = [] } = useZones();
  const { data: boxes = [] } = useBoxes();

  const [locationType, setLocationType] = useState<'zone' | 'box'>('zone');
  const [selectedZone, setSelectedZone] = useState('');
  const [selectedBox, setSelectedBox] = useState('');

  if (!isOpen) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      await moveBulkMutation.mutateAsync({
        objetIds: selectedIds,
        zoneId: locationType === 'zone' ? selectedZone : undefined,
        boxId: locationType === 'box' ? selectedBox : undefined,
      });
      alert(`${selectedIds.length} objet(s) déplacé(s) avec succès !`);
      onClose();
    } catch (error: any) {
      console.error('Erreur lors du déplacement en masse:', error);
      alert(`Erreur: ${error.response?.data?.message || error.message}`);
    }
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={onClose} />

        <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
          <form onSubmit={handleSubmit}>
            <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900">
                  🚚 Déplacer {selectedIds.length} objet(s)
                </h3>
                <button type="button" onClick={onClose} className="text-gray-400 hover:text-gray-500">
                  <span className="text-2xl">&times;</span>
                </button>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Type d'emplacement
                  </label>
                  <div className="grid grid-cols-2 gap-3">
                    <button
                      type="button"
                      onClick={() => setLocationType('zone')}
                      className={`p-3 border-2 rounded-lg transition-all ${
                        locationType === 'zone'
                          ? 'border-blue-500 bg-blue-50'
                          : 'border-gray-300 hover:border-gray-400'
                      }`}
                    >
                      <div className="text-2xl mb-1">🏠</div>
                      <div className="text-sm font-medium">Zone</div>
                    </button>
                    <button
                      type="button"
                      onClick={() => setLocationType('box')}
                      className={`p-3 border-2 rounded-lg transition-all ${
                        locationType === 'box'
                          ? 'border-blue-500 bg-blue-50'
                          : 'border-gray-300 hover:border-gray-400'
                      }`}
                    >
                      <div className="text-2xl mb-1">📦</div>
                      <div className="text-sm font-medium">Box</div>
                    </button>
                  </div>
                </div>

                {locationType === 'zone' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Zone de destination
                    </label>
                    <select
                      value={selectedZone}
                      onChange={(e) => setSelectedZone(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      required
                    >
                      <option value="">Sélectionner une zone</option>
                      {zones.map((zone: any) => (
                        <option key={zone.id} value={zone.id}>
                          {zone.code} - {zone.nom}
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                {locationType === 'box' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Box de destination
                    </label>
                    <select
                      value={selectedBox}
                      onChange={(e) => setSelectedBox(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      required
                    >
                      <option value="">Sélectionner une box</option>
                      {boxes.map((box: any) => (
                        <option key={box.id} value={box.id}>
                          {box.code} - {box.nom}
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                <div className="bg-yellow-50 border border-yellow-200 rounded-md p-3">
                  <p className="text-sm text-yellow-800">
                    ⚠️ Les {selectedIds.length} objets seront déplacés vers le même emplacement.
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
              <button
                type="submit"
                disabled={moveBulkMutation.isPending}
                className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-purple-600 text-base font-medium text-white hover:bg-purple-700 disabled:opacity-50 sm:ml-3 sm:w-auto sm:text-sm"
              >
                {moveBulkMutation.isPending ? 'Déplacement...' : 'Déplacer'}
              </button>
              <button
                type="button"
                onClick={onClose}
                className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
              >
                Annuler
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default BulkMoveModal;

